import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import LoadingIndicator from '../Components/LoadingIndicator';

const CustomerMaster = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: 'getCustomers',
        status: 'active'
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : (Array.isArray(data?.data) ? data.data : (Array.isArray(data?.customers) ? data.customers : [data]));

      const cleaned = list.filter(Boolean);
      setRows(cleaned);
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to load customers';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteModal = (row) => {
    setSelectedRow(row);
    setIsDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteOpen(false);
    setSelectedRow(null);
  };

  const handleDeleteCustomer = async () => {
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }
    const customerId = selectedRow?._id || selectedRow?.customerId;
    if (!customerId) {
      toast.error('Customer ID missing.');
      return;
    }
    setIsDeleting(true);
    try {
      const response = await axios.post(API_URL, {
        action: 'deleteCustomer',
        customerId
      }, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success(response.data?.message || 'Customer deleted');
      closeDeleteModal();
      await fetchCustomers();
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to delete customer';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const getCompanyName = (row) => row.companyName || '-';
  const getGst = (row) => row.gst || '-';
  const getContactPerson = (row) => row.contactPerson || '-';
  const getEmail = (row) => row.email || '-';
  const getMobileNumber = (row) => row.mobileNumber || '-';
  const getCity = (row) => row.city || '-';
  const getState = (row) => row.state || '-';
  const getPincode = (row) => row.pincode || '-';

  return (
    <div className="employee-form">
      <div className="content-header subscription-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Customer Master</h1>
            <p>View all customers.</p>
          </div>
          <div className="col-sm-6 text-right subscription-header-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/customer-master-create')}
            >
              Add Customer
            </button>
          </div>
        </div>
      </div>

      <div className="card employee-content-card">
        <div className="card-body">
          {isLoading ? (
            <div className="employee-loading">
              <LoadingIndicator size={120} />
            </div>
          ) : rows.length === 0 ? (
            <div className="employee-empty">No customer data found.</div>
          ) : (
            <div className="employee-table-wrapper">
              <table className="table table-borderless employee-table subscription-table">
                <thead>
                  <tr>
                    <th>Sl No.</th>
                    <th>Company Name</th>
                    <th>GST</th>
                    <th>Contact Person</th>
                    <th>Email</th>
                    <th>Mobile Number</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Pincode</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row._id || row.customerId || idx}>
                      <td>{idx + 1}</td>
                      <td>{getCompanyName(row)}</td>
                      <td>{getGst(row)}</td>
                      <td>{getContactPerson(row)}</td>
                      <td>{getEmail(row)}</td>
                      <td>{getMobileNumber(row)}</td>
                      <td>{getCity(row)}</td>
                      <td>{getState(row)}</td>
                      <td>{getPincode(row)}</td>
                      <td>
                        <div className="employee-actions">
                          <button
                            className="action-icon-btn view"
                            type="button"
                            title="View"
                            onClick={() => navigate(`/customer-master-view/${row._id || row.customerId}`)}
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                          <button
                            className="action-icon-btn delete"
                            type="button"
                            title="Delete"
                            onClick={() => openDeleteModal(row)}
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isDeleteOpen && (
        <div className="crop-overlay">
          <div className="crop-modal confirm-modal">
            <div className="crop-header">
              <h5>Delete Customer</h5>
              <button type="button" className="crop-close" onClick={closeDeleteModal}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="crop-body">
              <p className="confirm-message">
                Are you sure you want to delete {getCompanyName(selectedRow)}?
              </p>
            </div>
            <div className="crop-footer">
              <button type="button" className="btn btn-secondary" onClick={closeDeleteModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn confirm-delete-btn"
                onClick={handleDeleteCustomer}
                disabled={isDeleting}
              >
                {isDeleting ? <LoadingIndicator size={22} className="inline-loader" /> : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerMaster;
